import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, EVENTS_BATCH_SIZE } from '../constants/eventGrid';

export const useEventSearch = (initialFilters = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalEvents: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    city: '',
    priceRange: '',
    sortBy: 'date',
    sortOrder: 'asc',
    ...initialFilters
  });

  const [currentPage, setCurrentPage] = useState(1);
  const requestIdRef = useRef(0);

  const buildQueryParams = useCallback((search, filterState, page = 1) => {
    const params = new URLSearchParams();

    if (search) {
      params.append('search', search);
    }

    Object.entries(filterState).forEach(([key, value]) => {
      if (key === 'status') return;
      if (value && value !== '') {
        params.append(key, value);
      }
    });

    params.append('page', page.toString());
    params.append('limit', String(EVENTS_BATCH_SIZE));

    return params.toString();
  }, []);

  const fetchEvents = useCallback(async (search, filterState, page = 1, { append = false } = {}) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const queryParams = buildQueryParams(search, filterState, page);
      const response = await fetch(`${API_BASE}/api/events?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (requestId !== requestIdRef.current) return;

      if (data.success) {
        const apiEvents = data.data.events || [];
        setEvents((prev) => (append ? [...prev, ...apiEvents] : apiEvents));
        setPagination(
          data.data.pagination || {
            currentPage: page,
            totalPages: 1,
            totalEvents: apiEvents.length,
            hasNextPage: false,
            hasPrevPage: page > 1
          }
        );
        setCurrentPage(page);
      } else {
        setError(data.message || 'Failed to fetch events');
        if (!append) setEvents([]);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;

      const errorMessage = err.message.includes('Failed to fetch')
        ? 'Failed to connect to server. Make sure the backend server is running on port 5000.'
        : err.message.includes('HTTP error')
          ? `Server error: ${err.message}`
          : 'Failed to connect to server';
      setError(errorMessage);
      if (!append) setEvents([]);
      console.error('Error fetching events:', err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchEvents(searchQuery, filters, 1, { append: false });
  }, [searchQuery, filters, fetchEvents]);

  const search = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !pagination.hasNextPage) return;
    fetchEvents(searchQuery, filters, currentPage + 1, { append: true });
  }, [loading, loadingMore, pagination.hasNextPage, fetchEvents, searchQuery, filters, currentPage]);

  const clearAll = useCallback(() => {
    setSearchQuery('');
    setFilters({
      category: '',
      city: '',
      priceRange: '',
      sortBy: 'date',
      sortOrder: 'asc'
    });
    setCurrentPage(1);
  }, []);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (searchQuery) count++;
    if (filters.category) count++;
    if (filters.city) count++;
    if (filters.priceRange) count++;
    if (filters.sortBy !== 'date' || filters.sortOrder !== 'asc') count++;
    return count;
  }, [searchQuery, filters]);

  return {
    events,
    loading,
    loadingMore,
    error,
    pagination,
    searchQuery,
    filters,
    currentPage,
    search,
    updateFilters,
    loadMore,
    clearAll,
    getActiveFiltersCount: getActiveFiltersCount(),
    hasActiveFilters: getActiveFiltersCount() > 0
  };
};
